import { Preferences } from "@capacitor/preferences";
import { isNative } from "./platform";

/**
 * Key/value storage that works on web (localStorage) and native (Capacitor Preferences, which
 * survives WebView cache clears). All access is async so the backend can change without callers noticing.
 */
export interface KeyValueStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

const webStorage: KeyValueStorage = {
  get: (key) => {
    try {
      return Promise.resolve(localStorage.getItem(key));
    } catch {
      return Promise.resolve(null);
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      console.warn("[storage] write failed", err);
    }
    return Promise.resolve();
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return Promise.resolve();
  },
  keys: () => {
    try {
      return Promise.resolve(Object.keys(localStorage));
    } catch {
      return Promise.resolve([]);
    }
  },
};

const nativeStorage: KeyValueStorage = {
  get: async (key) => (await Preferences.get({ key })).value,
  set: (key, value) => Preferences.set({ key, value }),
  remove: (key) => Preferences.remove({ key }),
  keys: async () => (await Preferences.keys()).keys,
};

export const storage: KeyValueStorage = isNative ? nativeStorage : webStorage;

export async function getJson<T>(key: string): Promise<T | null> {
  const raw = await storage.get(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setJson(key: string, value: unknown): Promise<void> {
  return storage.set(key, JSON.stringify(value));
}

/** Remove every key under a prefix (account deletion wipes everything the game wrote). */
export async function removeByPrefix(prefix: string): Promise<string[]> {
  const matched = (await storage.keys()).filter((k) => k.startsWith(prefix));
  for (const key of matched) await storage.remove(key);
  return matched;
}
